import { useState, useEffect } from 'react';
import { Box, Skeleton, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import { BrokenImage as BrokenImageIcon, Close as CloseIcon, ZoomIn as ZoomInIcon, Description as DescriptionIcon } from '@mui/icons-material';
import { rxService } from '../../services/rxService';

interface RxImageThumbnailProps {
  pharmacyId: string;
  patientId: string;
  rxNumber: string;
  size?: number;
  borderRadius?: number;
  showPlaceholder?: boolean;
  expandable?: boolean;
}

/**
 * Displays an RX script image thumbnail.
 * Fetches the script image for the given RX number and displays it as a clickable thumbnail.
 * Same visual pattern as DrugImageThumbnail.
 */
export default function RxImageThumbnail({
  pharmacyId,
  patientId,
  rxNumber,
  size = 64,
  borderRadius = 4,
  showPlaceholder = false,
  expandable = false,
}: RxImageThumbnailProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  // PDF attachments (e.g. Rx-extension hard copies) can't render in an <img> —
  // they're shown via an <iframe> using the browser's built-in PDF viewer.
  const [isPdf, setIsPdf] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!pharmacyId || !patientId || !rxNumber) {
      setLoading(false);
      setError(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);
    setImageSrc(null);
    setIsPdf(false);

    (async () => {
      try {
        // Step 1: Find the script image prescription entry
        const prescription = await rxService.getRxScriptImageByNumber(pharmacyId, patientId, rxNumber);
        if (cancelled || !prescription?.scriptImageContentId) {
          if (!cancelled) { setError(true); setLoading(false); }
          return;
        }

        // Step 2: Fetch the actual image content
        const rxImage = await rxService.getRxImage(pharmacyId, patientId, prescription.scriptImageContentId);
        if (cancelled) return;

        if (rxImage?.content && rxImage?.mimeType) {
          const pdf = rxImage.mimeType.toLowerCase() === 'application/pdf';
          setIsPdf(pdf);
          // Blob URLs render PDFs far more reliably than multi-MB data URLs.
          setImageSrc(
            pdf
              ? rxService.createImageBlobUrl(rxImage.content, rxImage.mimeType)
              : `data:${rxImage.mimeType};base64,${rxImage.content}`,
          );
        } else {
          setError(true);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [pharmacyId, patientId, rxNumber]);

  // Free any blob URL we created (PDFs) when it's replaced or on unmount.
  useEffect(() => {
    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  if (!pharmacyId || !patientId || !rxNumber) return null;

  if (error) {
    if (!showPlaceholder) return null;
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: `${borderRadius}px`,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'grey.300',
          flexShrink: 0,
        }}
      >
        <BrokenImageIcon sx={{ fontSize: size * 0.4, color: 'grey.400' }} />
      </Box>
    );
  }

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        width={size}
        height={size}
        sx={{ borderRadius: `${borderRadius}px`, flexShrink: 0 }}
      />
    );
  }

  if (!imageSrc) return null;

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          flexShrink: 0,
          cursor: expandable ? 'pointer' : 'default',
          '&:hover .zoom-icon': expandable ? { opacity: 1 } : {},
        }}
        onClick={expandable ? (e) => { e.stopPropagation(); setExpanded(true); } : undefined}
      >
        {isPdf ? (
          <Box
            sx={{
              width: size,
              height: size,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
              borderRadius: `${borderRadius}px`,
              border: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'grey.50',
            }}
          >
            <DescriptionIcon sx={{ fontSize: size * 0.4, color: 'error.main' }} />
            <Typography sx={{ fontSize: size * 0.16, fontWeight: 700, color: 'text.secondary', lineHeight: 1 }}>
              PDF
            </Typography>
          </Box>
        ) : (
          <Box
            component="img"
            src={imageSrc}
            alt={`Rx ${rxNumber}`}
            sx={{
              width: size,
              height: size,
              objectFit: 'contain',
              borderRadius: `${borderRadius}px`,
              border: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'white',
            }}
          />
        )}
        {expandable && (
          <Box
            className="zoom-icon"
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 2,
              bgcolor: 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          >
            <ZoomInIcon sx={{ fontSize: 14, color: 'white' }} />
          </Box>
        )}
      </Box>

      {expandable && expanded && (
        <Dialog
          open={expanded}
          onClose={() => setExpanded(false)}
          maxWidth={false}
          slotProps={{
            paper: {
              sx: {
                bgcolor: 'transparent',
                boxShadow: 'none',
                overflow: 'visible',
              },
            },
          }}
        >
          <DialogContent
            sx={{
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              bgcolor: 'white',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <IconButton
              onClick={() => setExpanded(false)}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'rgba(0,0,0,0.4)',
                color: 'white',
                zIndex: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ p: 1, color: 'text.secondary' }}>
              <DescriptionIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              Rx #{rxNumber}
            </Typography>
            {isPdf ? (
              <Box
                component="iframe"
                src={imageSrc ?? undefined}
                title={`Rx ${rxNumber} PDF`}
                sx={{
                  width: '85vw',
                  height: '85vh',
                  border: 'none',
                  bgcolor: 'white',
                  display: 'block',
                }}
              />
            ) : (
              <Box
                component="img"
                src={imageSrc}
                alt={`Rx ${rxNumber}`}
                sx={{
                  maxWidth: '80vw',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
